import { useState, useEffect, useRef, useCallback } from 'react';
import Swal from 'sweetalert2';
import { submitAiLead } from '@/api/client';

// ─── Types ────────────────────────────────────────────────────────────────────

type StepId =
  | 'type_select'
  | 'name'
  | 'phone'
  | 'email'
  | 'address'
  | 'service'
  | 'date'
  | 'time'
  | 'notes'
  | 'summary';

interface Message {
  id: string;
  role: 'bot' | 'user';
  text: string;
}

interface LeadData {
  appointmentType: string;
  fullName: string;
  phone: string;
  email: string;
  address: string;
  serviceType: string;
  preferredDate: string;
  preferredTime: string;
  notes: string;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const LOGO_URL =
  'https://static.readdy.ai/image/e50d925263add9c47b947a5709171c8c/20f432f12014929b3d74941b5ec44146.png';

const SERVICES = [
  'Deep Cleaning',
  'Regular Cleaning',
  'Light Cleaning',
  'Airbnb Cabin Cleaning',
];

const TIMES = ['Morning', 'Afternoon'];

const WELCOME_MSG =
  'Hello! I\'m here to help you schedule a service. Would you like a phone call or an in-person estimate?';

const PHONE_FLOW: StepId[] = [
  'type_select',
  'name',
  'phone',
  'email',
  'service',
  'date',
  'time',
  'notes',
  'summary',
];

const INPERSON_FLOW: StepId[] = [
  'type_select',
  'name',
  'phone',
  'email',
  'address',
  'service',
  'date',
  'time',
  'notes',
  'summary',
];

const QUESTION: Record<StepId, string> = {
  type_select: WELCOME_MSG,
  name: 'What is your full name?',
  phone: 'What is your phone number?',
  email: 'What is your email address? (Optional — leave blank or type "skip" to continue)',
  address: 'What is the property address?',
  service: 'What service do you need?',
  date: 'What day would you prefer?',
  time: 'What time works best?',
  notes: 'Any additional notes or special requests? (Optional — type "skip" to continue)',
  summary: '',
};

const TEXT_STEPS: StepId[] = ['name', 'phone', 'email', 'address', 'date', 'notes'];

// ─── Small helpers ────────────────────────────────────────────────────────────

let msgCounter = 0;
function uid() {
  msgCounter += 1;
  return `msg-${Date.now()}-${msgCounter}`;
}

function botMsg(text: string): Message {
  return { id: uid(), role: 'bot', text };
}

function userMsg(text: string): Message {
  return { id: uid(), role: 'user', text };
}

function getNextStep(current: StepId, type: string): StepId {
  const flow = type === 'phone_call' ? PHONE_FLOW : INPERSON_FLOW;
  const idx = flow.indexOf(current);
  return flow[idx + 1] ?? 'summary';
}

function formatDate(raw: string): string {
  if (!raw) return raw;
  try {
    const d = new Date(raw + 'T12:00:00');
    return d.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return raw;
  }
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function TypingDots() {
  return (
    <div className="flex items-end gap-2 mb-3">
      <div className="w-7 h-7 rounded-full bg-brand-mint shrink-0 overflow-hidden">
        <img src={LOGO_URL} alt="" className="w-full h-full object-contain" />
      </div>
      <div className="bg-brand-light rounded-2xl rounded-bl-md px-4 py-3">
        <div className="flex gap-1">
          <span className="w-2 h-2 rounded-full bg-brand-mint animate-bounce" style={{ animationDelay: '0ms' }} />
          <span className="w-2 h-2 rounded-full bg-brand-mint animate-bounce" style={{ animationDelay: '150ms' }} />
          <span className="w-2 h-2 rounded-full bg-brand-mint animate-bounce" style={{ animationDelay: '300ms' }} />
        </div>
      </div>
    </div>
  );
}

interface BubbleProps {
  msg: Message;
}

function Bubble({ msg }: BubbleProps) {
  const isBot = msg.role === 'bot';
  return (
    <div className={`flex items-end gap-2 mb-3 ${isBot ? '' : 'flex-row-reverse'}`}>
      {isBot && (
        <div className="w-7 h-7 rounded-full bg-brand-mint shrink-0 overflow-hidden">
          <img src={LOGO_URL} alt="" className="w-full h-full object-contain" />
        </div>
      )}
      <div
        className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
          isBot
            ? 'bg-brand-light text-brand-dark rounded-bl-md'
            : 'bg-brand-dark text-white rounded-br-md'
        }`}
      >
        {msg.text}
      </div>
    </div>
  );
}

// ─── Main Widget ──────────────────────────────────────────────────────────────

export default function AiBookingWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [currentStep, setCurrentStep] = useState<StepId>('type_select');
  const [isTyping, setIsTyping] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [lead, setLead] = useState<LeadData>({
    appointmentType: '',
    fullName: '',
    phone: '',
    email: '',
    address: '',
    serviceType: '',
    preferredDate: '',
    preferredTime: '',
    notes: '',
  });

  const chatRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const tooltipTimer = useRef<number | null>(null);

  // ── Tooltip pulse ────────────────────────────────────────────────────────────
  useEffect(() => {
    const show = () => {
      setShowTooltip(true);
      tooltipTimer.current = window.setTimeout(() => setShowTooltip(false), 4000);
    };
    show();
    const t1 = window.setTimeout(show, 5000);
    const iv = window.setInterval(show, 14000);
    return () => {
      clearTimeout(t1);
      clearInterval(iv);
      if (tooltipTimer.current) clearTimeout(tooltipTimer.current);
    };
  }, []);

  // ── Scroll to bottom ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (chatRef.current) {
      chatRef.current.scrollTop = chatRef.current.scrollHeight;
    }
  }, [messages, isTyping, currentStep]);

  // ── Focus input when step changes to text ────────────────────────────────────
  useEffect(() => {
    if (!isTyping && TEXT_STEPS.includes(currentStep) && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [currentStep, isTyping]);

  // ── Helpers ──────────────────────────────────────────────────────────────────

  const appendBot = useCallback(
    (text: string, delay = 700) =>
      new Promise<void>((resolve) => {
        setIsTyping(true);
        setTimeout(() => {
          setMessages((prev) => [...prev, botMsg(text)]);
          setIsTyping(false);
          resolve();
        }, delay);
      }),
    [],
  );

  const advance = useCallback(
    (userText: string, answer: Partial<LeadData>, from: StepId, type: string) => {
      setMessages((prev) => [...prev, userMsg(userText)]);
      setInputValue('');

      const updatedLead = { ...lead, ...answer };
      setLead(updatedLead);

      const next = getNextStep(from, type);
      if (next === 'summary') {
        setIsTyping(true);
        setTimeout(() => {
          setIsTyping(false);
          setCurrentStep('summary');
        }, 700);
      } else {
        appendBot(QUESTION[next]).then(() => setCurrentStep(next));
      }
    },
    [lead, appendBot],
  );

  // ── Open / close ─────────────────────────────────────────────────────────────

  const handleOpen = () => {
    setOpen(true);
    setMessages([]);
    setCurrentStep('type_select');
    setLead({
      appointmentType: '',
      fullName: '',
      phone: '',
      email: '',
      address: '',
      serviceType: '',
      preferredDate: '',
      preferredTime: '',
      notes: '',
    });
    setInputValue('');
    setIsTyping(true);
    setShowTooltip(false);
    if (tooltipTimer.current) clearTimeout(tooltipTimer.current);

    setTimeout(() => {
      setMessages([botMsg(WELCOME_MSG)]);
      setIsTyping(false);
    }, 900);
  };

  const handleClose = useCallback(() => {
    setOpen(false);
    setIsTyping(false);
    setIsSubmitting(false);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') handleClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, handleClose]);

  // ── Step handlers ─────────────────────────────────────────────────────────────

  const handleTypeSelect = (type: string) => {
    const label = type === 'phone_call' ? 'Phone Call' : 'In-Person Estimate';
    setMessages((prev) => [...prev, userMsg(label)]);
    setLead((prev) => ({ ...prev, appointmentType: type }));
    appendBot(QUESTION['name']).then(() => setCurrentStep('name'));
  };

  const handleTextSubmit = () => {
    const val = inputValue.trim();
    if (!val) return;

    if (currentStep === 'name') {
      advance(val, { fullName: val }, 'name', lead.appointmentType);
    } else if (currentStep === 'phone') {
      advance(val, { phone: val }, 'phone', lead.appointmentType);
    } else if (currentStep === 'email') {
      const skip = val.toLowerCase() === 'skip' || val.toLowerCase() === 'no tengo' || val.toLowerCase() === 'no';
      const emailVal = skip ? '' : val;
      const displayVal = skip ? 'Skip' : val;
      advance(displayVal, { email: emailVal }, 'email', lead.appointmentType);
    } else if (currentStep === 'address') {
      advance(val, { address: val }, 'address', lead.appointmentType);
    } else if (currentStep === 'date') {
      const display = formatDate(val);
      advance(display, { preferredDate: val }, 'date', lead.appointmentType);
    } else if (currentStep === 'notes') {
      const skip = val.toLowerCase() === 'skip' || val.toLowerCase() === 'no' || val.toLowerCase() === 'none';
      const notesVal = skip ? '' : val;
      const displayVal = skip ? 'No additional notes' : val;
      advance(displayVal, { notes: notesVal }, 'notes', lead.appointmentType);
    }
  };

  const handleServiceSelect = (service: string) => {
    advance(service, { serviceType: service }, 'service', lead.appointmentType);
  };

  const handleTimeSelect = (time: string) => {
    advance(time, { preferredTime: time }, 'time', lead.appointmentType);
  };

  // ── Submit ────────────────────────────────────────────────────────────────────

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const result = await submitAiLead({
        full_name: lead.fullName,
        phone: lead.phone,
        email: lead.email || undefined,
        appointment_type: lead.appointmentType,
        service_type: lead.serviceType,
        property_address: lead.appointmentType === 'in_person' ? lead.address : undefined,
        preferred_date: lead.preferredDate || undefined,
        preferred_time: lead.preferredTime || undefined,
        notes: lead.notes || undefined,
      });

      if (result.success) {
        Swal.fire({
          title: 'Request Received!',
          text: 'Thank you. We received your request. We will contact you soon. For faster service, call (707) 623-6359.',
          icon: 'success',
          confirmButtonColor: '#3DBFA0',
          confirmButtonText: 'Great, thanks!',
        });
        handleClose();
      } else {
        Swal.fire({
          title: 'Something went wrong',
          text: result.message || 'Please try again or call us directly.',
          icon: 'error',
          confirmButtonColor: '#3DBFA0',
          confirmButtonText: 'OK',
        });
      }
    } catch {
      Swal.fire({
        title: 'Something went wrong',
        text: 'Please try again or call us directly.',
        icon: 'error',
        confirmButtonColor: '#3DBFA0',
        confirmButtonText: 'OK',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────────

  const isInPerson = lead.appointmentType === 'in_person';
  const showTextInput = !isTyping && TEXT_STEPS.includes(currentStep);

  return (
    <>
      {/* ── Floating Button ── */}
      <div className="fixed bottom-5 right-5 z-50 flex flex-col items-end gap-2">
        {showTooltip && (
          <div className="relative bg-white rounded-xl px-3 py-2 shadow-md border border-gray-100 text-xs text-brand-dark font-medium whitespace-nowrap">
            Need help booking?
            <div className="absolute -bottom-1 right-6 w-2 h-2 bg-white border-r border-b border-gray-100 rotate-45" />
          </div>
        )}
        <button
          onClick={handleOpen}
          className="group relative cursor-pointer"
          aria-label="Schedule Appointment"
        >
          <div className="absolute inset-0 rounded-3xl animate-pulse shadow-[0_0_25px_rgba(61,191,160,0.4)] group-hover:shadow-[0_0_35px_rgba(61,191,160,0.6)] transition-shadow duration-300" />
          <div className="animate-bounce-slow transition-transform duration-300 group-hover:scale-110">
            <img
              src={LOGO_URL}
              alt="Schedule"
              className="w-[55px] h-[55px] sm:w-[75px] sm:h-[75px] lg:w-[85px] lg:h-[85px] object-contain drop-shadow-lg"
            />
          </div>
        </button>
      </div>

      {/* ── Chat Panel ── */}
      {open && (
        <div
          className="fixed bottom-0 right-0 sm:bottom-5 sm:right-5 z-[70] w-full sm:w-[400px] h-[100dvh] sm:h-auto sm:max-h-[580px] flex flex-col"
          role="dialog"
          aria-modal="true"
        >
          <div className="flex flex-col w-full h-full sm:rounded-2xl bg-white shadow-2xl border border-gray-100 overflow-hidden">

            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 shrink-0 bg-white">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-brand-mint shrink-0 overflow-hidden">
                  <img src={LOGO_URL} alt="" className="w-full h-full object-contain" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-brand-dark">Scheduling Assistant</h3>
                  <p className="text-xs text-brand-muted">We help you book the right service</p>
                </div>
              </div>
              <button
                onClick={handleClose}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-brand-light transition-colors cursor-pointer"
                aria-label="Close"
              >
                <i className="ri-close-line text-lg text-brand-dark" />
              </button>
            </div>

            {/* Messages */}
            <div ref={chatRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-1">
              {messages.map((m) => (
                <Bubble key={m.id} msg={m} />
              ))}

              {isTyping && <TypingDots />}

              {/* ── Type select buttons ── */}
              {currentStep === 'type_select' && !isTyping && (
                <div className="flex flex-col gap-2 pl-9 mt-1">
                  <button
                    onClick={() => handleTypeSelect('phone_call')}
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-gray-200 text-sm font-medium text-brand-dark hover:border-brand-mint hover:bg-brand-mint/5 transition-all cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-phone-line text-brand-mint" />
                    Phone Call
                  </button>
                  <button
                    onClick={() => handleTypeSelect('in_person')}
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-gray-200 text-sm font-medium text-brand-dark hover:border-brand-mint hover:bg-brand-mint/5 transition-all cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-home-4-line text-brand-mint" />
                    In-Person Estimate
                  </button>
                </div>
              )}

              {/* ── Service buttons ── */}
              {currentStep === 'service' && !isTyping && (
                <div className="flex flex-col gap-2 pl-9 mt-1">
                  {SERVICES.map((s) => (
                    <button
                      key={s}
                      onClick={() => handleServiceSelect(s)}
                      className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-gray-200 text-sm font-medium text-brand-dark hover:border-brand-mint hover:bg-brand-mint/5 transition-all cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-sparkling-line text-brand-mint" />
                      {s}
                    </button>
                  ))}
                </div>
              )}

              {/* ── Email input ── */}
              {currentStep === 'email' && !isTyping && (
                <div className="flex flex-col gap-2 pl-9 mt-1">
                  <button
                    onClick={() => {
                      setMessages((prev) => [...prev, userMsg('Skip')]);
                      setInputValue('');
                      const updatedLead = { ...lead, email: '' };
                      setLead(updatedLead);
                      const next = getNextStep('email', lead.appointmentType);
                      appendBot(QUESTION[next]).then(() => setCurrentStep(next));
                    }}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-dashed border-gray-300 text-xs font-medium text-brand-muted hover:border-brand-mint hover:text-brand-dark transition-all cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-skip-forward-line text-brand-mint" />
                    Skip (no email)
                  </button>
                </div>
              )}

              {/* ── Notes skip ── */}
              {currentStep === 'notes' && !isTyping && (
                <div className="flex flex-col gap-2 pl-9 mt-1">
                  <button
                    onClick={() => {
                      setMessages((prev) => [...prev, userMsg('No additional notes')]);
                      setInputValue('');
                      const updatedLead = { ...lead, notes: '' };
                      setLead(updatedLead);
                      const next = getNextStep('notes', lead.appointmentType);
                      if (next === 'summary') {
                        setIsTyping(true);
                        setTimeout(() => { setIsTyping(false); setCurrentStep('summary'); }, 700);
                      } else {
                        appendBot(QUESTION[next]).then(() => setCurrentStep(next));
                      }
                    }}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-dashed border-gray-300 text-xs font-medium text-brand-muted hover:border-brand-mint hover:text-brand-dark transition-all cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-skip-forward-line text-brand-mint" />
                    Skip (no notes)
                  </button>
                </div>
              )}

              {/* ── Time buttons ── */}
              {currentStep === 'time' && !isTyping && (
                <div className="flex gap-2 pl-9 mt-1">
                  {TIMES.map((t) => (
                    <button
                      key={t}
                      onClick={() => handleTimeSelect(t)}
                      className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-gray-200 text-sm font-medium text-brand-dark hover:border-brand-mint hover:bg-brand-mint/5 transition-all cursor-pointer whitespace-nowrap"
                    >
                      <i className={`${t === 'Morning' ? 'ri-sun-line' : 'ri-sun-foggy-line'} text-brand-mint`} />
                      {t}
                    </button>
                  ))}
                </div>
              )}

              {/* ── Summary ── */}
              {currentStep === 'summary' && !isTyping && (
                <div className="pl-9 mt-1">
                  <div className="bg-brand-light rounded-2xl rounded-bl-md px-4 py-4 text-sm text-brand-dark">
                    <p className="font-semibold mb-3 text-brand-dark">Here&apos;s a summary of your request:</p>
                    <div className="space-y-2">
                      <SummaryRow icon="ri-user-line" label="Name" value={lead.fullName} />
                      <SummaryRow icon="ri-phone-line" label="Phone" value={lead.phone} />
                      {lead.email && (
                        <SummaryRow icon="ri-mail-line" label="Email" value={lead.email} />
                      )}
                      {isInPerson && (
                        <SummaryRow icon="ri-map-pin-line" label="Address" value={lead.address} />
                      )}
                      <SummaryRow icon="ri-sparkling-line" label="Service" value={lead.serviceType} />
                      <SummaryRow icon="ri-calendar-line" label="Preferred day" value={lead.preferredDate ? formatDate(lead.preferredDate) : lead.preferredDate} />
                      <SummaryRow icon="ri-time-line" label="Preferred time" value={lead.preferredTime} />
                      {lead.notes && (
                        <SummaryRow icon="ri-file-text-line" label="Notes" value={lead.notes} />
                      )}
                    </div>
                    <button
                      onClick={handleSubmit}
                      disabled={isSubmitting}
                      className="mt-4 w-full flex items-center justify-center gap-2 px-5 py-2.5 bg-brand-dark text-white text-sm font-semibold rounded-xl hover:bg-brand-mint transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
                    >
                      {isSubmitting ? (
                        <>
                          <i className="ri-loader-4-line animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <i className="ri-send-plane-line" />
                          Submit Request
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}

              {/* Scroll anchor */}
              <div className="h-1" />
            </div>

            {/* ── Text Input Bar ── */}
            {showTextInput && (
              <div className="shrink-0 border-t border-gray-100 px-4 py-3 bg-white">
                {(currentStep === 'email' || currentStep === 'notes') && (
                  <p className="text-xs text-brand-muted mb-2 flex items-center gap-1">
                    <i className="ri-information-line text-brand-mint" />
                    {currentStep === 'email' ? 'Optional — tap Skip if you prefer not to share' : 'Optional — tap Skip to continue'}
                  </p>
                )}
                <div className="flex items-center gap-2">
                  {currentStep === 'email' ? (
                    <input
                      ref={inputRef}
                      type="text"
                      inputMode="email"
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleTextSubmit(); }}
                      placeholder="your@email.com (optional)"
                      className="flex-1 px-3 py-2 rounded-xl border border-gray-200 text-sm text-brand-dark focus:outline-none focus:border-brand-mint focus:ring-1 focus:ring-brand-mint bg-white transition-colors placeholder:text-gray-400"
                    />
                  ) : currentStep === 'notes' ? (
                    <input
                      ref={inputRef}
                      type="text"
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleTextSubmit(); }}
                      placeholder="Any special requests..."
                      className="flex-1 px-3 py-2 rounded-xl border border-gray-200 text-sm text-brand-dark focus:outline-none focus:border-brand-mint focus:ring-1 focus:ring-brand-mint bg-white transition-colors placeholder:text-gray-400"
                    />
                  ) : currentStep === 'date' ? (
                    <input
                      ref={inputRef}
                      type="date"
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleTextSubmit(); }}
                      className="flex-1 px-3 py-2 rounded-xl border border-gray-200 text-sm text-brand-dark focus:outline-none focus:border-brand-mint focus:ring-1 focus:ring-brand-mint bg-white transition-colors"
                    />
                  ) : (
                    <input
                      ref={inputRef}
                      type={currentStep === 'phone' ? 'tel' : 'text'}
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleTextSubmit(); }}
                      placeholder={
                        currentStep === 'name'
                          ? 'Type your name...'
                          : currentStep === 'phone'
                          ? '(707) 000-0000'
                          : currentStep === 'address'
                          ? '123 Main St, City, CA'
                          : ''
                      }
                      className="flex-1 px-3 py-2 rounded-xl border border-gray-200 text-sm text-brand-dark focus:outline-none focus:border-brand-mint focus:ring-1 focus:ring-brand-mint bg-white transition-colors placeholder:text-gray-400"
                    />
                  )}
                  <button
                    onClick={handleTextSubmit}
                    disabled={!inputValue.trim()}
                    className="w-9 h-9 flex items-center justify-center bg-brand-dark text-white rounded-xl hover:bg-brand-mint transition-colors cursor-pointer disabled:opacity-40 shrink-0"
                  >
                    <i className="ri-arrow-right-line text-base" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

// ─── Summary Row ──────────────────────────────────────────────────────────────

function SummaryRow({ icon, label, value }: { icon: string; label: string; value: string }) {
  return (
    <div className="flex items-start gap-2">
      <i className={`${icon} text-brand-mint mt-0.5 shrink-0`} />
      <div className="flex gap-1 flex-wrap">
        <span className="text-brand-muted text-xs">{label}:</span>
        <span className="font-medium text-xs text-brand-dark">{value || '—'}</span>
      </div>
    </div>
  );
}